/**
 * How much the HUD tells him, by difficulty (04 §2, 08 §2.7). The owner's rule: "as you go higher difficulty
 * you shouldn't see the radius around you that shows you how much noise you're making". This is fair because
 * none of it is information the world withholds - the noise radii are fixed per action (`NOISE.crawl` 1.5,
 * walk 6, run 12, a shot 45), the ground changes the footstep sound, and men bark and turn when they hear
 * something. Taking the readout away tests what he has learnt; it never asks him to guess.
 *
 * It is a LADDER, not a switch, and it only sets the DEFAULT: a player who leans on visual sound cues
 * (08 §4, "visualise sounds") forces either aid back on from Options and nothing about the campaign changes.
 * SOUND PIPS are deliberately NOT on the ladder: they are the only visual the deaf player has for a noise he
 * did not make, and they are where the Tinnitus trait bites (`SimMods.hearingRadius`, pips.ts).
 */

/** What a player's Options setting says. `auto` follows the difficulty; `on` / `off` override it. */
export type HudAid = 'auto' | 'on' | 'off';

/** `persist`: a ring for every footstep, as it has always been. `flash`: only when it says something new. */
export type RingMode = 'persist' | 'flash' | 'off';

export interface AidLevel {
  ring: RingMode;
  arcs: boolean;
}

/** 0 Milk Run, 1 Standard, 2 Hard, 3 Suicide (`Operation.difficulty`). */
export const DIFFICULTY_AIDS: readonly AidLevel[] = [
  { ring: 'persist', arcs: true },
  { ring: 'flash', arcs: true },
  { ring: 'off', arcs: true },
  // Suicide: only the world tells him. The ? / ! glyphs go with the arcs they hang on.
  { ring: 'off', arcs: false },
];

/** Standard: what Practice, Classic and the Daily Drop get, having no operation difficulty of their own. */
export const DEFAULT_DIFFICULTY = 1;

export const aidsFor = (difficulty: number): AidLevel =>
  DIFFICULTY_AIDS[Math.min(DIFFICULTY_AIDS.length - 1, Math.max(0, Math.round(difficulty)))]
  ?? { ring: 'flash', arcs: true };

/**
 * A setting the menus have not caught up with yet is a plain boolean: `true` is the untouched default, which
 * means "follow the difficulty", and `false` is a player who has switched the aid off and meant it.
 */
const asAid = (setting: HudAid | boolean): HudAid => (typeof setting === 'boolean' ? (setting ? 'auto' : 'off') : setting);

/** The noise ring the player actually gets: his setting, or the difficulty's default when it is on `auto`. */
export function ringMode(setting: HudAid | boolean, difficulty: number): RingMode {
  const aid = asAid(setting);
  if (aid === 'off') return 'off';
  // Forced on: the fullest form, whatever the difficulty - this is the accessibility escape hatch.
  if (aid === 'on') return 'persist';
  return aidsFor(difficulty).ring;
}

/** The detection arcs he actually gets. */
export function arcsOn(setting: HudAid | boolean, difficulty: number): boolean {
  const aid = asAid(setting);
  if (aid === 'off') return false;
  if (aid === 'on') return true;
  return aidsFor(difficulty).arcs;
}

/** How long a `flash` ring waits before the same gait is worth showing again, ms. */
export const FLASH_REARM_MS = 2500;

/** The last footstep ring raised, so `flash` can tell a new lesson from the same one repeating. */
export interface StepRingMemory {
  radius: number;
  at: number;
}

/**
 * Should this footstep raise a ring? `persist` always; `off` never; `flash` when he has just started moving or
 * changed gait - the moment the noise changes - and again only after `FLASH_REARM_MS`, so the ring teaches what
 * an action costs without standing under him for the whole mission.
 */
export function stepRingShows(mode: RingMode, radius: number, last: StepRingMemory, now: number): boolean {
  if (mode === 'off' || radius <= 0) return false;
  if (mode === 'persist') return true;
  return radius !== last.radius || now - last.at >= FLASH_REARM_MS;
}
